import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotificationsUnreadCounts, updateStatusNotification } from '../../services/dashboard';

/**
 * Persistent dashboard banner for 90%+ subscription usage alerts.
 *
 * Only the "High Priority Warning" (90%) and "Limit Reached" (100%) tiers
 * ever reach this component — the worker that generates these
 * (check_usage_limits.js) only sets is_important:true at those two
 * thresholds, and the unread-counts endpoint's `important_alerts` field
 * filters on exactly that flag. 50%/75% notifications still reach the user
 * through the existing notification bell in Navbar — this banner isn't meant
 * to duplicate those, only to make the two urgent tiers impossible to miss,
 * per the ticket's "display persistent alerts" requirement for 90%+.
 *
 * Deliberately does its own fetch rather than reading the notification bell's
 * redux state: that state is capped to the 5 most recent notifications of ANY
 * type (see store/reducers/notifications), so a usage alert can fall off it
 * the moment a handful of unrelated notifications land afterward. A banner
 * that can silently disappear because of unrelated activity defeats the
 * "persistent" requirement, so this reads its own untruncated feed instead.
 */
/** Beyond this the banner stops being an alert and starts being the page. */
const MAX_VISIBLE_ALERTS = 3;

/**
 * Defence in depth: one entry per condition, even if the API sends duplicates.
 *
 * The server already collapses these (see collapseDuplicateAlerts in
 * notifications/services.js), so this is normally a no-op. It exists because
 * the two deploy independently — a frontend shipped ahead of the backend would
 * otherwise still stack every unread copy and break the layout, which is the
 * exact bug being fixed.
 *
 * Groups on the same key the server uses, and merges duplicate_ids so a
 * dismissal still retires the whole set.
 */
function dedupeAlerts(list = []) {
	const groups = new Map();

	for (const alert of list) {
		const feature = alert.metadata?.feature;
		const threshold = alert.metadata?.threshold;
		const key =
			feature !== undefined && threshold !== undefined
				? `${alert.type}::${feature}::${threshold}`
				: `${alert.type}::${alert.title}`;

		const existing = groups.get(key);

		if (!existing) {
			groups.set(key, { ...alert, duplicate_ids: alert.duplicate_ids ?? [alert.id] });
			continue;
		}

		for (const id of alert.duplicate_ids ?? [alert.id]) {
			if (!existing.duplicate_ids.includes(id)) existing.duplicate_ids.push(id);
		}
	}

	return [...groups.values()].slice(0, MAX_VISIBLE_ALERTS);
}

const UsageLimitBanner = () => {
	const navigate = useNavigate();
	const [alerts, setAlerts] = useState([]);

	useEffect(() => {
		let cancelled = false;

		async function fetchImportantAlerts() {
			try {
				const { important_alerts } = await getNotificationsUnreadCounts();
				if (!cancelled) {
					setAlerts(dedupeAlerts(important_alerts || []));
				}
			} catch (error) {
				// Silent — a failed fetch here shouldn't block the rest of the
				// dashboard from rendering, and the notification bell is an
				// independent fallback for the same underlying data.
			}
		}

		fetchImportantAlerts();

		return () => {
			cancelled = true;
		};
	}, []);

	const handleDismiss = async (alert) => {
		setAlerts((prev) => prev.filter((item) => item.id !== alert.id));

		// Retire EVERY row in the group, not just the one on screen.
		//
		// The banner shows one entry per condition, but several unread rows can
		// describe that same condition (the usage worker inserted a copy hourly
		// before its dedupe was fixed). Marking only the visible id as read left
		// the rest unread, so the next fetch promoted a sibling and the alert
		// appeared to come straight back — the "should not immediately reappear"
		// complaint. `duplicate_ids` always contains at least the visible id.
		const ids = alert.duplicate_ids?.length ? alert.duplicate_ids : [alert.id];

		try {
			await updateStatusNotification({
				status: 'is_read',
				value: true,
				ids,
				mark_as_read: false,
			});
		} catch (error) {
			// Already removed from view — a failed mark-as-read just means it
			// may resurface on next load, not a broken banner right now.
		}
	};

	const handleUpgrade = () => {
		navigate('/subscription-plan');
	};

	if (alerts.length === 0) return null;

	return (
		<div
			className="d-flex flex-column gap-2 p-3"
			style={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				zIndex: 999,
				pointerEvents: 'none',
			}}
		>
			{alerts.map((alert) => {
				const isLimitReached = alert.metadata?.threshold >= 100;

				return (
					<div
						key={alert.id}
						className={`alert ${isLimitReached ? 'alert-danger' : 'alert-warning'} d-flex align-items-center justify-content-between mb-0 gap-3 shadow`}
						style={{ pointerEvents: 'auto' }}
						role="alert"
					>
						{/* min-width:0 lets this column shrink below its text's intrinsic
						    width. Without it a flex item refuses to go narrower than its
						    content, so the long description pushed the Upgrade/dismiss
						    buttons out past the edge of the content column on smaller
						    screens instead of wrapping. */}
						<div style={{ minWidth: 0 }}>
							<strong>{alert.title}</strong>
							<div className="fs-6">{alert.description}</div>
						</div>
						<div className="d-flex align-items-center gap-2 flex-shrink-0">
							<button
								type="button"
								className={`btn btn-sm ${isLimitReached ? 'btn-danger' : 'btn-warning'}`}
								onClick={handleUpgrade}
							>
								Upgrade Plan
							</button>
							<button
								type="button"
								className="btn-close"
								aria-label="Dismiss"
								onClick={() => handleDismiss(alert)}
							/>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default UsageLimitBanner;
